
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { someValueInFormIsEmpty } from "@/utils/methods";
import { SMCases, SMQuestionnaire, SMTasks } from "@/store";
import { TProvideDataSelectAnswerAddressOptionType } from "@/modules/Questionnaire/components/AnswerBlocks/TAnswerBlocks";
import { SPONSOR_LICENCE_SERVICE_TYPE } from "@/constants/services";

const ADDRESS_QUESTION_TYPE = "address";
const PAYE_QUESTION_TYPE = "paye";
const DEPENDANTS_QUESTION_TYPE = "dependants";

const PAYE_NUMBER_MASK = "###/AXNNNNNNNN";

const AVAILABLE_COUNTRIES = ["United Kingdom of Great Britain and Northern Ireland"];

const FORM = {
  [ADDRESS_QUESTION_TYPE]: {
    address_type: "",
    name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    country: "",
    postcode: "",
    county: "",
    // website: "",
    // phone: "",
  },
  [PAYE_QUESTION_TYPE]: {
    paye_reference_number: "",
    name: "",
  },
  [DEPENDANTS_QUESTION_TYPE]: {
    first_name: "",
    last_name: "",
  },
};

const TRADING_ADDRESS_ADDRESS_TYPE = "trading_address";
const BRANCH_ADDRESS_ADDRESS_TYPE = "branch";

const FORM_OPTIONALS_DEFAULT = ["address_line_2", "county"];
const FORM_OPTIONALS_TRADING_NAME = ["name", "address_line_2", "county"];

@Component
export default class ProvideDataSelectAddNew extends Vue {
  @Prop({ type: String, default: "" }) readonly questionType?: string;
  @Prop({ type: Boolean, default: false }) readonly editing?: boolean;
  @Prop({ type: Array, default: () => [] })
  readonly addressTypeValues?: TProvideDataSelectAnswerAddressOptionType[];

  form = FORM;

  PAYE_NUMBER_MASK = PAYE_NUMBER_MASK;

  get addressTypeOptions() {
    return this.addressTypeValues?.reduce((acc, cur) => {
      const item = {
        value: cur.type,
        label: cur.name,
        disabled: !cur.enabled,
      };

      acc.push(item);

      return acc;
    }, []);
  }

  setForm(type: string) {
    this.form = { ...FORM[type] };

    this.form["address_type"] = BRANCH_ADDRESS_ADDRESS_TYPE;
  }

  @Watch("questionType", { deep: true, immediate: true })
  updateAnswer(value: string) {
    if (!value) return;

    this.setForm(value);
  }

  get title() {
    const { questionType } = this;

    return questionType ? this.$tc(`title.${questionType}`) : this.$tc("title.address");
  }

  loading = false;

  get enableSubmitAddAnotherValue() {
    const form = (this.form as unknown) as any;

    const exceptions =
      (form?.address_type as string) === TRADING_ADDRESS_ADDRESS_TYPE
        ? FORM_OPTIONALS_TRADING_NAME
        : FORM_OPTIONALS_DEFAULT;

    return !someValueInFormIsEmpty(this.form, exceptions);
  }

  get isSLCase() {
    return SPONSOR_LICENCE_SERVICE_TYPE === SMCases.caseType;
  }

  get showAddressType() {
    return this.isSLCase;
  }

  get showBranchName() {
    const form = (this.form as unknown) as any;

    return form?.address_type && (form?.address_type as string) === BRANCH_ADDRESS_ADDRESS_TYPE;
  }

  countriesList = [];

  async getCountriesList() {
    const { data } = await this.axios.get(API_ROUTES.GET_COUNTRIES_LIST());

    if (!data) return;

    this.countriesList = data;
  }

  get preparedCountriesList() {
    return this.countriesList.reduce((acc, cur) => {
      if (AVAILABLE_COUNTRIES?.length) {
        if (AVAILABLE_COUNTRIES.includes(cur)) acc.push({ label: cur, value: cur });
      } else {
        acc.push({ label: cur, value: cur });
      }

      return acc;
    }, []);
  }

  mounted() {
    this.getCountriesList();

    this.$root.$on("questionnaire-go-previous-question", () => {
      this.cancel();
    });
  }

  cancel() {
    this.setForm(this.questionType);

    this.$emit("cancel");

    this.$emit("update:editing", false);
  }

  async submitAddAnotherValue() {
    try {
      this.loading = true;

      const payload = { ...this.form };

      const userFlowID = SMQuestionnaire.userflowID ?? SMTasks.taskUserflow;

      if (!userFlowID) return;

      const url =
        this.questionType === ADDRESS_QUESTION_TYPE
          ? API_ROUTES.ADD_COMPANY_ADDRESS_VALUE()
          : API_ROUTES.ADD_COMPANY_QUESTION_VALUE(userFlowID);

      if (this.questionType === ADDRESS_QUESTION_TYPE) {
        Object.assign(payload, {
          user_flow_id: userFlowID,
        });
      }

      const { data } = await this.axios.post(url, payload);

      this.$emit("update:editing", false);

      await this.$nextTick();

      SMQuestionnaire.setAnswerBlockEditing(false);

      this.$emit("another-value-added", data);
    } finally {
      this.loading = false;

      this.$emit("update:editing", false);
    }
  }
}
